<template>
  <Teleport to="body">
    <div class="swiper-body">
      <button class="swiper-close" @click="close">
        <i class="fas fa-times"></i>
      </button>
      <div class="swiper-container" ref="swiper">
        <div class="swiper-wrapper">
          <div class="swiper-slide" v-for="(file, idx) in files" :key="idx">
            <div v-if="getFileType(file) === 'image'" class="swiper-zoom-container">
              <img
                v-if="file.thumbnail_link"
                :src="file.link"
                :alt="file.filename"
                @dragstart.prevent
                class="full-image"
                :style="{ backgroundImage: `url(${file.thumbnail_link})` }"
              />
              <img
                v-else
                :src="file.link"
                :alt="file.filename"
                @dragstart.prevent
                class="full-image"
              />
            </div>
            <video
              v-else-if="getFileType(file) === 'video'"
              controls
              playsinline
              loop
              muted
              @dragstart.prevent
              :ref="'video-' + idx"
            >
              <source :src="file.link" />
              Your browser does not support the video tag.
            </video>
            <div
              v-else
              class="generic-file"
            >
              <i class="far fa-file"></i>
              <div class="filename">{{ truncateText(file.filename, 30) }}</div>
              <a :href="file.link" target="_blank" rel="noopener" class="download-btn">
                <i class="fas fa-download"></i> Download
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="swiper-pagination">
        <span
          v-for="(file, idx) in files"
          :key="idx"
          class="swiper-pagination-bullet"
          :class="{ 'swiper-pagination-bullet-active': idx === currentSlide }"
          @click="goToSlide(idx)"
        ></span>
      </div>
      <div class="file-details" v-if="currentFile">
        <span class="filename">{{ truncateText(currentFile.filename, 30) }}</span>
        <span class="separator">|</span>
        <span class="nowrap">{{ formatTimestamp(currentFile) }}</span>
        <span class="separator" v-if="currentFile.file_size > 0">|</span>
        <span class="nowrap" v-if="currentFile.file_size > 0">
          {{ formatFileSize(currentFile.file_size) }}
        </span>
      </div>
    </div>
  </Teleport>
</template>

<script>
import Teleport from 'vue2-teleport';
import Swiper from 'swiper';
import 'swiper/css/swiper.css';
import moment from 'moment';
import { getFileType, truncateText, formatFileSize } from '@/helpers';

export default {
  components: {
    Teleport,
  },
  data() {
    return {
      currentSlide: this.index || 0,
    };
  },
  computed: {
    currentFile() {
      return this.files[this.currentSlide] || null;
    },
  },
  props: {
    files: Array,
    index: {
      type: Number,
      default: null,
    },
  },
  mounted() {
    document.body.style.overflow = 'hidden';
    this.$nextTick(() => {
      this.initSwiper();
    });
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    if (this.swiper) {
      this.swiper.destroy();
    }
  },
  methods: {
    getFileType(file) {
      return getFileType(file);
    },
    truncateText(text, maxLength) {
      return truncateText(text, maxLength);
    },
    formatFileSize(size) {
      return formatFileSize(size);
    },
    formatTimestamp(file) {
      const timestamp = file.mtime_ts === 0 ? file.upload_ts : file.mtime_ts;
      return moment(timestamp * 1000).format('DD.MM.YY HH:mm:ss');
    },
    initSwiper() {
      if (!this.$refs.swiper) {
        console.error('Swiper ref not found');
        return;
      }
      this.swiper = new Swiper(this.$refs.swiper, {
        direction: 'vertical',
        slidesPerView: 1,
        spaceBetween: 0,
        mousewheel: true,
        initialSlide: this.index || 0,
        touchRatio: 1,
        touchAngle: 45,
        simulateTouch: true,
        shortSwipes: true,
        longSwipes: true,
        longSwipesRatio: 0.5,
        longSwipesMs: 300,
        followFinger: true,
        threshold: 5,
        touchMoveStopPropagation: false,
        touchStartPreventDefault: false,
        touchStartForcePreventDefault: false,
        touchReleaseOnEdges: false,
        resistance: true,
        resistanceRatio: 0.85,
        zoom: {
          maxRatio: 3,
          minRatio: 1,
        },
      });
      this.swiper.on('slideChange', () => {
        this.currentSlide = this.swiper.activeIndex;
        this.handleVideoPlayback();
      });
      this.currentSlide = this.index || 0;
      this.$nextTick(() => {
        this.handleVideoPlayback();
      });
    },
    goToSlide(index) {
      if (this.swiper) {
        this.swiper.slideTo(index);
      }
    },
    close() {
      this.$emit('close');
    },
    handleVideoPlayback() {
      this.files.forEach((file, idx) => {
        if (this.getFileType(file) === 'video') {
          const videoId = `video-${idx}`;
          const videoRef = this.$refs[videoId];
          const video = Array.isArray(videoRef) ? videoRef[0] : videoRef;
          if (video) {
            if (idx === this.currentSlide) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }
        }
      });
    },
  },
};
</script>

<style lang="scss">
  .swiper-body {
    position: fixed;
    inset: 0;
    z-index: 1000;
    width: 100%;
    height: 100%;
  }
  .swiper-container {
    display: block;
    width: 100%;
    height: 100%;
    touch-action: pan-y;
  }
  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #000;

    img {
      max-width: 100%;
      max-height: 100vh;
      width: auto;
      height: auto;
      object-fit: contain;

      &.full-image {
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }
    }

    video {
      max-width: 100%;
      max-height: calc(100vh - 40px);
      width: auto;
      height: auto;
      padding-bottom: 40px;
    }

    .generic-file {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
      color: white;
      padding: 40px;
      text-align: center;

      i.fa-file {
        font-size: 80px;
        color: #ccc;
      }

      .filename {
        font-size: 16px;
        word-break: break-word;
        max-width: 80vw;
      }

      .download-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 8px;
        text-decoration: none;
        font-size: 14px;
        transition: all 0.2s;

        &:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
        }
      }
    }
  }

  .swiper-close {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 1001;
    background: rgba(0, 0, 0, 0.5);
    border: none;
    color: white;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(0, 0, 0, 0.8);
    }
  }

  .swiper-pagination {
    position: fixed;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .swiper-pagination-bullet {
    background: rgba(255, 255, 255, 0.5);
    opacity: 1;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
  }

  .swiper-pagination-bullet-active {
    background: white;
    width: 10px;
    height: 10px;
  }

  .file-details {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.8);
    font-size: 12px;
    color: #fff;
    height: 40px;
    text-align: center;
    z-index: 1001;
    padding: 0 60px;
    overflow: hidden;
    white-space: nowrap;

    .filename {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 40%;
    }

    .nowrap {
      white-space: nowrap;
      flex-shrink: 0;
    }

    .separator {
      padding: 0 8px;
      color: #ccc;
      flex-shrink: 0;
    }
  }
</style>
